import { BorrowerInformation } from 'pages/LoanApplication/BorrowerInformation'
import { BorrowerLabel, BorrowerSeperator } from 'pages/LoanApplication/config'
import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

/**
 * Borrower Information inside servicing.
 *
 * This renders the loan application's own BorrowerInformation section rather than a copy of it, so
 * there are no second set of fields and nothing to keep in sync: a servicer editing an address here
 * writes the same borrower record that origination reads, through the same save path. The menu entry
 * used to navigate out to /loan_process/application/:loan?menu=borrower, which meant leaving the loan
 * in servicing to change a phone number.
 *
 * The one thing servicing has to supply itself is the borrower selector. The application page spends
 * these across its left menu (Borrower 1, Borrower 2, …); servicing has one menu slot, so the
 * borrowers become a row of tabs above the form. Ordering and labels come from the application's own
 * helpers so the two pages agree on which borrower is "Borrower 2".
 */
export default function ServicingBorrowerInformation({ setLoading }: { setLoading: (loading: boolean) => void }) {
  const borrowers = useSelector((state: any) => state.borrower)

  /** Included borrowers first, matching the application page's ordering. */
  const borrowerSepList = useMemo(() => {
    const keys = Object.keys(borrowers || {}) as BorrowerSeperator[]
    const isIncluded = (sep: BorrowerSeperator) =>
      sep === BorrowerSeperator.BORROWER || borrowers[sep]?.isIncluded !== false
    return [...keys.filter(isIncluded), ...keys.filter((sep) => !isIncluded(sep))]
  }, [borrowers])

  const labels = useMemo(() => BorrowerLabel(), [borrowers])
  const [selected, setSelected] = useState<BorrowerSeperator>(BorrowerSeperator.BORROWER)

  // A borrower can be removed in origination while servicing still has it selected.
  const active = borrowerSepList.includes(selected) ? selected : borrowerSepList[0] ?? BorrowerSeperator.BORROWER

  return (
    <div>
      {borrowerSepList.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-4 border-b pb-3">
          {borrowerSepList.map((sep) => {
            const isActive = sep === active
            const name = [borrowers[sep]?.firstName, borrowers[sep]?.lastName].filter(Boolean).join(' ')
            const excluded = sep !== BorrowerSeperator.BORROWER && borrowers[sep]?.isIncluded === false
            return (
              <button
                key={sep}
                type="button"
                onClick={() => setSelected(sep)}
                className={`px-4 py-1 rounded text-sm hover:underline ${
                  isActive ? 'border bg-zinc-100 font-semibold' : 'text-shade-blue'
                }`}
              >
                {labels[sep] || sep}
                {name ? ` - ${name}` : ''}
                {excluded ? ' (not included)' : ''}
              </button>
            )
          })}
        </div>
      )}

      {/* Keyed so switching borrower remounts the form instead of leaving the previous one's values. */}
      <BorrowerInformation key={active} borrowerSeperator={active} setLoading={setLoading} />
    </div>
  )
}
